<template>
  <el-dialog
    v-model="visible"
    :title="title"
    :width="width"
    :close-on-click-modal="false"
    class="ai-assistant-dialog"
  >
    <div class="dialog-container">
      <!-- 历史记录侧边栏 -->
      <div class="history-sidebar">
        <div class="sidebar-header">
          <h3>历史记录</h3>
          <el-button type="primary" link @click="startNewChat">
            <el-icon><Plus /></el-icon>
            新对话
          </el-button>
        </div>
        <div class="history-list">
          <div v-for="chat in chatHistory" 
               :key="chat.id" 
               class="history-item"
               :class="{ active: chat.id === currentChatId }"
               @click="selectChat(chat.id)">
            <div class="history-item-content">
              <div class="history-item-title">{{ chat.name || '新对话' }}</div>
              <div class="history-item-time">{{ formatTime(chat.updated_at) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- 聊天主区域 -->
      <div class="chat-main">
        <div class="chat-messages" ref="messagesContainer">
          <div v-for="msg in currentMessages" :key="msg.id" :class="['message', msg.role]">
            <div class="message-content">
              <div class="message-header">
                <span class="message-sender">
                  {{ msg.role === 'user' ? '🧑 你' : '🤖 AI助手' }}
                </span>
                <span class="message-time">{{ formatTime(msg.created_at) }}</span>
              </div>
              <div class="message-text" :class="{ 
                'streaming': isStreaming && msg.id === streamingMessageId,
                'thinking': msg.isThinking 
              }">
                {{ msg.content }}
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input">
          <el-input
            v-model="input"
            type="textarea"
            :rows="3"
            placeholder="请输入您的问题..."
            @keyup.enter.ctrl="send"
            :disabled="loading"
            ref="inputRef"
          />
          <div class="input-actions">
            <span class="input-tip">按 Ctrl + Enter 发送</span>
            <el-button type="primary" @click="send" :loading="loading" :disabled="!input.trim()">
              发送
            </el-button>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script setup>
import { ref, onMounted, nextTick, watch, onUnmounted } from 'vue'
import { Plus } from '@element-plus/icons-vue'
import { ElMessage } from 'element-plus'
import request from '@/utils/request'
import { clearAllAuthState } from '@/utils/auth'
import { getStoragePrefix } from '@/utils/domain'
import router from '@/router'

import { validateModuleType } from '@/constants/ai-assistant'

/**
 * AI助手对话框组件
 * @component AIAssistantDialog
 * @property {string} moduleType - 模块类型，可选值：'GENERAL'|'SALES'|'MAINTENANCE'|'MEDICAL'|'OFFICE'
 * @property {string} userId - 用户ID
 * @property {string} [title='AI助手'] - 对话框标题
 * @property {string} [width='800px'] - 对话框宽度
 */

// 格式化时间
const formatTime = (timestamp) => {
  if (!timestamp) return '';
  const date = new Date(timestamp * 1000);
  const now = new Date();
  const diff = now - date;
  
  // 如果是今天
  if (diff < 24 * 60 * 60 * 1000) {
    return date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  }
  
  // 如果是昨天
  if (diff < 48 * 60 * 60 * 1000) {
    return '昨天 ' + date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  }
  
  // 如果是今年
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString('zh-CN', { month: '2-digit', day: '2-digit' }) + ' ' + 
           date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
  }
  
  // 其他情况
  return date.toLocaleDateString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit' }) + ' ' + 
         date.toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' });
}

const props = defineProps({
  moduleType: {
    type: String,
    required: true,
    validator: validateModuleType
  },
  userId: {
    type: [String, Number],  // 允许字符串或数字类型
    required: true,
    validator: (value) => {
      // 确保值不为空且可以转换为字符串
      return value != null && String(value).trim() !== ''
    }
  },
  title: {
    type: String,
    default: '智能助手'
  },
  width: {
    type: String,
    default: '800px'
  }
})

// 验证必要的属性
const validateProps = () => {
  if (!props.moduleType) {
    ElMessage.error('未指定模块类型')
    return false
  }
  if (!props.userId) {
    ElMessage.error('未指定用户ID')
    return false
  }
  return true
}

const visible = ref(false)
const input = ref('')
const loading = ref(false)
const isStreaming = ref(false)
const streamingMessageId = ref(null)
const streamingContent = ref('')
const isAgentMode = ref(false)  // 新增：是否为Agent模式
const agentThought = ref('')    // 新增：Agent的思考内容
const messagesContainer = ref(null)
const chatHistory = ref([])
const currentChatId = ref(null)
const currentMessages = ref([])
const inputRef = ref(null)  // 新增：输入框的ref

// 添加超时检测变量
const messageTimeout = ref(null)
const MAX_MESSAGE_TIMEOUT = 30000 // 30秒超时

// 重置所有状态的函数
const resetAllStates = () => {
  console.log('[AI助手对话框] 重置所有状态')
  loading.value = false
  isStreaming.value = false
  streamingMessageId.value = null
  streamingContent.value = ''
  if (isAgentMode.value) {
    agentThought.value = ''
    isAgentMode.value = false
  }
  if (messageTimeout.value) {
    clearTimeout(messageTimeout.value)
    messageTimeout.value = null
  }
  // 新增：聚焦到输入框
  nextTick(() => {
    inputRef.value?.input?.focus()
  })
}

// 获取历史记录
const fetchHistory = async () => {
  if (!validateProps()) return
  
  const normalizedUserId = String(props.userId)  // 确保 userId 是字符串
  
  console.log('[AI助手对话框] 开始获取历史记录:', {
    moduleType: props.moduleType,
    userId: normalizedUserId,
    currentChatId: currentChatId.value,
    currentMessages: currentMessages.value.length,
    routePath: window.location.pathname,
    routeQuery: window.location.search,
    timestamp: new Date().toISOString()
  })
  
  try {
    const response = await request.get('/v1/office/ai/chat/history', {
      params: {
        moduleType: props.moduleType,
        userId: normalizedUserId
      }
    })
    
    console.log('[AI助手对话框] 获取历史记录响应:', {
      status: response.status,
      statusText: response.statusText,
      data: response.data,
      moduleType: props.moduleType,
      timestamp: new Date().toISOString()
    })
    
    if (response.code === 0) {
      chatHistory.value = response.data || []
      console.log('[AI助手对话框] 更新后的历史记录:', {
        count: chatHistory.value.length,
        items: chatHistory.value.map(chat => ({
          id: chat.id,
          name: chat.name,
          status: chat.status,
          created_at: chat.created_at,
          updated_at: chat.updated_at
        })),
        moduleType: props.moduleType,
        timestamp: new Date().toISOString()
      })

      // 如果有当前对话，检查是否需要重新加载消息
      if (currentChatId.value) {
        const currentChat = chatHistory.value.find(chat => chat.id === currentChatId.value)
        if (!currentChat) {
          // 如果当前对话不在历史记录中，清空当前对话
          currentChatId.value = null
          currentMessages.value = []
        }
      }
    }
  } catch (error) {
    console.error('[AI助手对话框] 获取历史记录失败:', {
      error: error.message,
      moduleType: props.moduleType,
      userId: normalizedUserId,
      stack: error.stack,
      timestamp: new Date().toISOString()
    })
    
    if (error.response?.status === 401) {
      clearAllAuthState()
      router.push('/login')
      return
    }
    
    ElMessage.error('获取历史记录失败')
  }
}

// 选择对话
const selectChat = async (chatId) => {
  if (!chatId) return
  
  const normalizedUserId = String(props.userId)  // 确保 userId 是字符串
  
  console.log('[AI助手对话框] 开始选择对话:', {
    chatId,
    moduleType: props.moduleType,
    userId: normalizedUserId,
    timestamp: new Date().toISOString()
  })
  
  currentChatId.value = chatId
  currentMessages.value = []
  
  try {
    const response = await request.get(`/v1/office/ai/chat/${chatId}/messages`, {
      params: {
        moduleType: props.moduleType,
        userId: normalizedUserId
      }
    })
    
    if (response.code === 0) {
      const messages = Array.isArray(response.data) ? response.data : []
      currentMessages.value = messages
      
      console.log('[AI助手对话框] 更新消息列表:', {
        chatId,
        messageCount: messages.length,
        messages: messages.map(msg => ({
          role: msg.role,
          content: msg.content?.substring(0, 100) + '...',
          created_at: msg.created_at
        })),
        moduleType: props.moduleType,
        timestamp: new Date().toISOString()
      })
    }
  } catch (error) {
    console.error('[AI助手对话框] 加载对话消息失败:', {
      error: error.message,
      chatId,
      moduleType: props.moduleType,
      userId: normalizedUserId,
      stack: error.stack,
      timestamp: new Date().toISOString()
    })
    
    if (error.response?.status === 401) {
      clearAllAuthState()
      router.push('/login')
      return
    }
  }
}


// 开始新对话
const startNewChat = () => {
  currentChatId.value = null
  currentMessages.value = []
  input.value = ''
}

// 处理不同类型的事件
const handleStreamEvent = (parsedData) => {
  console.log('[AI助手对话框] 处理事件:', {
    event: parsedData.event,
    messageId: parsedData.message_id,
    timestamp: new Date().toISOString()
  })

  switch (parsedData.event) {
    case 'agent_thought':
      // 检测到agent_thought事件，说明是Agent模式
      isAgentMode.value = true
      agentThought.value = parsedData.thought || ''
      // 添加思考消息
      if (!streamingMessageId.value) {
        streamingMessageId.value = parsedData.message_id
        currentMessages.value.push({
          role: 'assistant',
          content: '🤔 思考中...\n' + agentThought.value,
          message_id: parsedData.message_id,
          created_at: parsedData.created_at,
          isThinking: true
        })
      }
      break

    case 'agent_message':
      // Agent模式的消息处理
      if (!streamingMessageId.value) {
        streamingMessageId.value = parsedData.message_id
        streamingContent.value = ''
        // 如果存在思考消息，更新它；否则添加新消息
        const lastMessage = currentMessages.value[currentMessages.value.length - 1]
        if (lastMessage?.isThinking) {
          lastMessage.content = parsedData.answer || ''
          lastMessage.isThinking = false
        } else {
          currentMessages.value.push({
            role: 'assistant',
            content: parsedData.answer || '',
            message_id: parsedData.message_id,
            created_at: parsedData.created_at
          })
        }
      } else if (parsedData.message_id === streamingMessageId.value) {
        streamingContent.value += parsedData.answer || ''
        const lastMessage = currentMessages.value[currentMessages.value.length - 1]
        if (lastMessage && lastMessage.message_id === parsedData.message_id) {
          lastMessage.content = streamingContent.value
        }
      }
      break

    case 'message':
      // 普通对话模式的消息处理
      isAgentMode.value = false
      if (!streamingMessageId.value) {
        streamingMessageId.value = parsedData.message_id
        streamingContent.value = ''
        currentMessages.value.push({
          role: 'assistant',
          content: parsedData.answer || '',
          message_id: parsedData.message_id,
          created_at: parsedData.created_at
        })
      } else if (parsedData.message_id === streamingMessageId.value) {
        streamingContent.value += parsedData.answer || ''
        const lastMessage = currentMessages.value[currentMessages.value.length - 1]
        if (lastMessage && lastMessage.message_id === parsedData.message_id) {
          lastMessage.content = streamingContent.value
        }
      }
      break

    case 'message_end':
      console.log('[AI助手对话框] 收到消息结束事件:', {
        messageId: parsedData.message_id,
        conversationId: parsedData.conversation_id,
        timestamp: new Date().toISOString()
      })
      
      // 保存元数据
      if (parsedData.metadata) {
        const lastMessage = currentMessages.value[currentMessages.value.length - 1]
        if (lastMessage) {
          lastMessage.metadata = parsedData.metadata
        }
      }

      // 设置会话ID
      if (parsedData.conversation_id && !currentChatId.value) {
        currentChatId.value = parsedData.conversation_id
      }

      // 使用统一的状态重置函数
      resetAllStates()
      break

    case 'error':
      console.error('[AI助手对话框] 流式响应错误:', parsedData)
      ElMessage.error(parsedData.message || '接收消息失败')
      resetAllStates()
      break
  }
}

// 发送消息
const send = async () => {
  if (!validateProps()) return
  
  if (!input.value.trim()) {
    ElMessage.warning('请输入消息内容')
    return
  }

  const normalizedUserId = String(props.userId)
  const userMessage = input.value
  let shouldClearInput = true

  console.log('[AI助手对话框] 开始发送消息:', {
    message: userMessage,
    moduleType: props.moduleType,
    userId: normalizedUserId,
    conversationId: currentChatId.value,
    timestamp: new Date().toISOString()
  })

  try {
    loading.value = true
    isStreaming.value = true
    
    // 设置超时检测
    messageTimeout.value = setTimeout(() => {
      console.warn('[AI助手对话框] 消息响应超时')
      ElMessage.warning('响应超时，请重试')
      resetAllStates()
    }, MAX_MESSAGE_TIMEOUT)

    // 先添加用户消息到对话列表
    currentMessages.value.push({
      role: 'user',
      content: userMessage,
      created_at: Date.now() / 1000 // 转换为秒级时间戳
    })

    // 清空输入框
    input.value = ''

    // 创建 EventSource 实例
    const baseURL = request.defaults.baseURL || ''
    const token = localStorage.getItem(`${getStoragePrefix()}_token`)
    
    // 检查登录状态
    if (!token) {
      console.error('[AI助手对话框] 未找到token，准备跳转登录页', {
        currentUrl: window.location.href,
        baseURL,
        timestamp: new Date().toISOString()
      })
      
      // 保存当前页面URL
      localStorage.setItem('redirect_url', window.location.href)
      
      // 清理认证状态并跳转
      clearAllAuthState()
      router.push({
        path: '/login',
        query: {
          redirect: window.location.pathname + window.location.search
        }
      })
      return
    }

    // 检查token格式
    console.log('[AI助手对话框] Token检查:', {
      hasToken: !!token,
      tokenLength: token?.length,
      startsWithBearer: token?.startsWith('Bearer '),
      currentUrl: window.location.href,
      timestamp: new Date().toISOString()
    })

    console.log('[AI助手对话框] 发送请求:', {
      url: `${baseURL}/v1/office/ai/chat/message`,
      moduleType: props.moduleType,
      userId: normalizedUserId,
      conversationId: currentChatId.value,
      hasToken: !!token,
      currentUrl: window.location.href,
      timestamp: new Date().toISOString()
    })

    // 构建认证头
    const authToken = token.startsWith('Bearer ') ? token : `Bearer ${token}`
    
    const response = await fetch(`${baseURL}/v1/office/ai/chat/message`, {
      method: 'POST',
      credentials: 'include', // 添加这行以包含cookies
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authToken,
        'X-Module-Type': props.moduleType,
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        message: userMessage,
        moduleType: props.moduleType,
        userId: normalizedUserId,
        conversationId: currentChatId.value,
        streaming: true
      })
    })

    // 检查响应状态
    if (!response.ok) {
      const errorText = await response.text().catch(() => '无法获取错误详情')
      console.error('[AI助手对话框] 请求失败:', {
        status: response.status,
        statusText: response.statusText,
        error: errorText,
        url: `${baseURL}/v1/office/ai/chat/message`,
        currentUrl: window.location.href,
        hasToken: !!token,
        tokenLength: token?.length,
        timestamp: new Date().toISOString()
      })

      if (response.status === 401) {
        console.warn('[AI助手对话框] 认证失败，准备重新登录', {
          currentUrl: window.location.href,
          timestamp: new Date().toISOString()
        })
        
        // 保存当前页面URL
        localStorage.setItem('redirect_url', window.location.href)
        
        // 清理认证状态并跳转
        clearAllAuthState()
        router.push({
          path: '/login',
          query: {
            redirect: window.location.pathname + window.location.search
          }
        })
        return
      }

      // 处理其他错误
      const errorMessage = errorText.includes('{') 
        ? JSON.parse(errorText).message || '请求失败'
        : `请求失败: ${response.status} - ${errorText}`
      
      throw new Error(errorMessage)
    }

    // 读取响应流
    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''
    let lastEventTime = Date.now()
    // eslint-disable-next-line
    while (true) {
      const { done, value } = await reader.read()
      
      if (Date.now() - lastEventTime > 10000) {
        console.warn('[AI助手对话框] 消息块接收超时')
        throw new Error('消息接收超时')
      }

      if (done) {
        console.log('[AI助手对话框] 流读取完成')
        resetAllStates()
        break
      }

      lastEventTime = Date.now()
      
      buffer += decoder.decode(value, { stream: true })
      const lines = buffer.split('\n')
      buffer = lines.pop() || ''

      for (const line of lines) {
        if (line.trim() === '') continue
        if (!line.startsWith('data: ')) continue

        const data = line.slice(6)
        if (data === '[DONE]') {
          console.log('[AI助手对话框] 收到[DONE]标记')
          resetAllStates()
          continue
        }

        try {
          const parsedData = JSON.parse(data)
          handleStreamEvent(parsedData)
        } catch (error) {
          console.warn('[AI助手对话框] 解析流式数据失败:', {
            error: error.message,
            data,
            timestamp: new Date().toISOString()
          })
        }
      }
    }
  } catch (error) {
    console.error('[AI助手对话框] 发送消息失败:', {
      error: error.message,
      stack: error.stack,
      timestamp: new Date().toISOString()
    })
    
    if (currentMessages.value.length > 0 && 
        currentMessages.value[currentMessages.value.length - 1].role === 'user') {
      currentMessages.value.pop()
    }
    
    ElMessage.error(error.message || '发送消息失败')
    
    if (shouldClearInput) {
      input.value = userMessage
    }
    
    resetAllStates()
  } finally {
    resetAllStates()
  }
}

// 滚动到底部
const scrollToBottom = () => {
  if (messagesContainer.value) {
    nextTick(() => {
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    })
  }
}

// 监听对话框显示状态
watch(() => props.modelValue, async (newVal) => {
  if (newVal) {
    // 对话框显示时刷新历史记录
    await fetchHistory()
  }
})

// 监听模块类型变化
watch(() => props.moduleType, async (newVal, oldVal) => {
  if (newVal !== oldVal) {
    // 重置状态
    currentChatId.value = null
    currentMessages.value = []
    chatHistory.value = []
    
    // 重新获取历史记录
    await fetchHistory()
  }
})

// 监听用户ID变化
watch(() => props.userId, (newId) => {
  console.log('[AI助手对话框] 用户ID变更:', newId)
  fetchHistory() // 切换用户时重新获取历史记录
})

// 监听对话框显示状态
watch(visible, (newVal, oldVal) => {
  console.log('[AI助手对话框] 对话框状态变化:', { 
    from: oldVal,
    to: newVal,
    moduleType: props.moduleType,
    userId: props.userId,
    currentChatId: currentChatId.value,
    routePath: window.location.pathname,
    timestamp: new Date().toISOString()
  })
  if (newVal) {
    // 每次打开对话框时，重新获取历史记录
    currentChatId.value = null
    currentMessages.value = []
    fetchHistory()
  }
})

// 监听消息变化，自动滚动到底部
watch(currentMessages, () => {
  nextTick(() => {
    scrollToBottom()
  })
}, { deep: true })

// 在组件卸载时清理
onUnmounted(() => {
  if (messageTimeout.value) {
    clearTimeout(messageTimeout.value)
  }
})

onMounted(() => {
  console.log('[AI助手对话框] 组件挂载:', {
    moduleType: props.moduleType,
    userId: props.userId,
    routePath: window.location.pathname,
    timestamp: new Date().toISOString()
  })
})
</script>

<style lang="scss" scoped>
.ai-assistant-dialog {
  :deep(.el-dialog__body) {
    padding: 0;
  }
}

.dialog-container {
  display: flex;
  height: 600px;
}

.history-sidebar {
  width: 280px;
  border-right: 1px solid #dcdfe6;
  display: flex;
  flex-direction: column;

  .sidebar-header {
    padding: 16px;
    border-bottom: 1px solid #dcdfe6;
    display: flex;
    justify-content: space-between;
    align-items: center;

    h3 {
      margin: 0;
      font-size: 16px;
    }
  }

  .history-list {
    flex: 1;
    overflow-y: auto;
    padding: 10px;
    max-height: 300px;
  }
}

.chat-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #f5f7fa;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;

  .message {
    margin-bottom: 16px;
    display: flex;
    flex-direction: column;

    &.user {
      align-items: flex-end;
    }

    &.assistant {
      align-items: flex-start;
    }

    .message-content {
      max-width: 80%;
      padding: 12px 16px;
      border-radius: 8px;
      background-color: #fff;
      box-shadow: 0 2px 4px rgba(0,0,0,0.05);

      .message-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 8px;
        font-size: 12px;
        color: #909399;
      }

      .message-text {
        white-space: pre-wrap;
        word-break: break-word;

        &.streaming {
          border-right: 2px solid #409EFF;
          animation: cursor-blink 1s infinite;
        }

        &.thinking {
          color: #909399;
          font-style: italic;
          background-color: #f5f7fa;
          padding: 8px;
          border-radius: 4px;
          border-left: 3px solid #409EFF;
        }
      }
    }
  }
}

.chat-input {
  padding: 16px;
  background-color: #fff;
  border-top: 1px solid #dcdfe6;

  .input-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 8px;

    .input-tip {
      font-size: 12px;
      color: #909399;
    }
  }
}

@keyframes cursor-blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0; }
}

.history-item {
  padding: 10px;
  margin-bottom: 8px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  background: #f5f7fa;
}

.history-item:hover {
  background: #e4e7ed;
}

.history-item.active {
  background: #409eff;
  color: white;
}

.history-item-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.history-item-title {
  font-size: 14px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.history-item-time {
  font-size: 12px;
  color: #909399;
}

.history-item.active .history-item-time {
  color: rgba(255, 255, 255, 0.8);
}
</style> 